// CollaboratorsPanel — Feb 23, 2026.
//
// Lives inside SiloSettings. Lets the silo OWNER:
//   • Search for another user (by email or name) and invite them as a collaborator.
//   • See the current collaborator list (with leave/remove controls).
//   • Toggle 50/50 revenue split on fan-sub income (only meaningful when a
//     collaborator is present).
//
// Collaborators see this panel too, but most controls are read-only for them —
// they only get a "Leave this silo" button on their own row.
import { useEffect, useRef, useState } from "react";
import { Users, MagnifyingGlass, X, UserPlus, Trash, CheckCircle, Circle, Scales, Spinner, ShareNetwork, Copy, ShieldWarning, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser } from "@/lib/api";
import InfoTip from "@/components/InfoTip";
import LockGate from "@/components/LockGate";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { useEntitlements } from "@/lib/entitlements";
import { SiloKindPill, SiloKindConvertModal, resolveSiloKind, SILO_KIND_META } from "@/components/SiloKindPill";

export default function CollaboratorsPanel({ silo, onSiloUpdated }) {
  const [data, setData] = useState(null);   // {owner, collaborators, pending_invites, revenue_split_enabled, is_owner}
  const [loading, setLoading] = useState(true);
  const [splitSummary, setSplitSummary] = useState(null);   // {per_collaborator: [...]}
  const [settling, setSettling] = useState(false);
  // Jul 2026 (Randy) — child-seeded invites + shareable link.
  const [inviteAsChild, setInviteAsChild] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [minting, setMinting] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  // Feb 19, 2026 (Randy) — inline silo-type convert modal, opened by the
  // "Wrong silo type?" shortcut above the invite input.
  const [typeConvertOpen, setTypeConvertOpen] = useState(false);
  const isPersonal = silo?.silo_kind === "personal";
  const { locked: cpLocked, lockStyle: cpLockStyle } = useEntitlements();
  const { openGate: cpOpenGate } = useCreatorProGate();

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/silos/${silo.id}/collaborators`),
        api.get(`/silos/${silo.id}/split-summary`).catch(() => ({ data: { per_collaborator: [] } })),
      ]);
      setData(r.data);
      setSplitSummary(s.data || { per_collaborator: [] });
      if (isPersonal && r.data?.is_owner) {
        try {
          const fm = await api.get(`/silos/${silo.id}/family-members`);
          setFamilyMembers(fm.data?.members || []);
        } catch { setFamilyMembers([]); }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load collaborators.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [silo.id]);

  // ----- Search + invite -----
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debRef = useRef(null);
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if ((query || "").trim().length < 2) { setResults([]); return; }
    debRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/users/search", { params: { q: query.trim() } });
        setResults(r.data?.results || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(debRef.current);
  }, [query]);

  const invite = async (user) => {
    // Feb 2026 (Randy pt-invite-clicktime-precheck) — confirm before firing.
    const label = user.name ? `${user.name} <${user.email}>` : user.email;
    const roleNote = isPersonal && inviteAsChild
      ? "as a KID collaborator (purchase guardrails + no PIN required)"
      : "as a collaborator on this silo";
    if (!window.confirm(`Invite ${label} ${roleNote}?`)) return;
    try {
      const r = await api.post(`/silos/${silo.id}/invites`, {
        invitee_id: user.id,
        is_child: isPersonal && inviteAsChild,
      });
      if (r.data?.downgraded) {
        toast(`${user.name || user.email} is already a paying adult — invited normally (the child restriction can't apply).`, { id: "invite-downgraded", duration: 6000 });
      } else {
        toast.success(`Invite sent to ${user.name || user.email}.`);
      }
      setQuery("");
      setResults([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send invite.");
    }
  };

  const mintShareLink = async () => {
    setMinting(true);
    try {
      const r = await api.post(`/silos/${silo.id}/family-share-link`, { is_child: inviteAsChild });
      const url = `${window.location.origin}/join-family/${r.data.token}`;
      setShareUrl(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create link.");
    } finally { setMinting(false); }
  };

  const copyOrShare = async () => {
    if (!shareUrl) return;
    const shareData = { title: `Join ${silo.name} on My Silo`, url: shareUrl };
    // On phones, open the native share sheet so they pick their own messaging
    // app; on desktop, fall back to clipboard copy.
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch { /* cancelled → fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — paste it into any message.");
    } catch {
      toast("Copy failed — select the link and copy it manually.");
    }
  };

  const releaseFamilyMember = async (m) => {
    if (!window.confirm(`Release ${m.child_name}? They keep household access but the purchase guardrail is removed (marks them as an adult).`)) return;
    try {
      await api.post(`/silos/${silo.id}/family-links/${m.link_id}/release`);
      toast.success(`${m.child_name} released — they're now unrestricted.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Release failed."); }
  };

  const cancelInvite = async (invId) => {
    try {
      await api.delete(`/silos/${silo.id}/invites/${invId}`);
      toast("Invite cancelled.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed."); }
  };

  const remove = async (userId, label, isSelf) => {
    const msg = isSelf
      ? "Leave this silo? You'll lose access until invited again."
      : `Remove ${label} from this silo?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/silos/${silo.id}/collaborators/${userId}`);
      toast.success(isSelf ? "Left the silo." : `Removed ${label}.`);
      if (isSelf) window.location.assign("/dashboard");
      else load();
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed."); }
  };

  const toggleSplit = async () => {
    if (!data) return;
    const next = !data.revenue_split_enabled;
    if (next && (data.collaborators || []).length === 0) {
      toast.error("Add a collaborator first — splits need someone to split with.");
      return;
    }
    try {
      await api.patch(`/silos/${silo.id}/revenue-split`, { enabled: next });
      toast.success(next ? "Equal split is ON." : "Equal split turned off (100% goes to you).");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Toggle failed."); }
  };

  const settleNow = async () => {
    setSettling(true);
    try {
      const r = await api.post(`/silos/${silo.id}/settle-splits`);
      const { settled, failed, skipped_no_account, total_settled_cents, failures } = r.data || {};
      if (settled > 0) {
        toast.success(`Sent $${(total_settled_cents / 100).toFixed(2)} to ${settled} transfer${settled === 1 ? "" : "s"}.`);
      }
      if (skipped_no_account > 0) {
        toast(`${skipped_no_account} collaborator${skipped_no_account === 1 ? "" : "s"} haven't finished Stripe onboarding yet — owed amounts wait until they do.`, { id: "skipped-no-acct" });
      }
      if (failed > 0) {
        toast.error(`${failed} transfer${failed === 1 ? "" : "s"} failed: ${(failures?.[0]?.error || "").slice(0, 90)}`);
      }
      if (!settled && !failed && !skipped_no_account) {
        toast("Nothing to settle — everyone's paid up.");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Settle failed.");
    } finally { setSettling(false); }
  };

  if (loading || !data) {
    return (
      <div className="bg-[#FDFCF8] border hairline rounded-lg p-6 text-sm text-[#6B6A68] flex items-center gap-2" data-testid="collab-panel-loading">
        <Spinner size={14} className="animate-spin" /> Loading collaborators…
      </div>
    );
  }

  const isOwner = !!data.is_owner;
  // Creator Pro gate (Jul 2026) — managing collaborators from settings needs the
  // `silo_collab_settings` SKU. Only the OWNER sees the gated controls; a locked
  // owner sees them shimmered and a click opens the buffet. Collaborators (and
  // the read-only roster) are never gated. Backend enforces the wall regardless.
  const collabLocked = isOwner && cpLocked("silo_collab_settings");
  const collabs = data.collaborators || [];
  const pending = data.pending_invites || [];
  // Identify "me" reliably (collab panel may include several collaborators;
  // pulling the first one was wrong when there are 2+).
  const meUser = getUser();
  const myId = meUser?.id;

  return (
    <>
    <section className="bg-[#FDFCF8] border hairline rounded-lg p-6" data-testid="collab-panel">
      <div className="flex items-center gap-2 mb-1">
        <Users size={16} weight="duotone" className="text-[#4A5D4E]" />
        <h3 className="font-serif-display text-lg text-[#1C1B1A]">Collaborators</h3>
        <InfoTip text="Invite another person to share this silo. They'll see it on their dashboard alongside their own silos. Everything you add — documents, refinery passes, push pings, the question bank — they see too. The slug, subscriber price, and silo deletion stay with you." />
      </div>
      <p className="text-xs text-[#6B6A68] mb-4 max-w-2xl leading-relaxed">
        Same silo, two doors. Whatever your collaborator adds becomes part of the silo immediately — no copying, no syncing.
      </p>

      {/* Owner & collaborator list */}
      <div className="space-y-1.5">
        {/* Owner row */}
        <div className="flex items-center gap-3 bg-white border hairline rounded px-3 py-2 text-sm" data-testid="collab-row-owner">
          <CheckCircle size={14} weight="duotone" className="text-[#2F6B3A]" />
          <div className="flex-1 min-w-0">
            <div className="text-[#1C1B1A] truncate"><strong>{data.owner?.name || data.owner?.email}</strong> <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] ml-2">owner</span></div>
            <div className="text-[11px] text-[#6B6A68] truncate">{data.owner?.email}</div>
          </div>
        </div>
        {collabs.map((c) => {
          const isMe = myId && c.id === myId;
          return (
            <div key={c.id} className="flex items-center gap-3 bg-white border hairline rounded px-3 py-2 text-sm" data-testid={`collab-row-${c.id}`}>
              <Users size={14} weight="duotone" className="text-[#4A5D4E]" />
              <div className="flex-1 min-w-0">
                <div className="text-[#1C1B1A] truncate">
                  <strong>{c.name || c.email}</strong>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] ml-2">{isMe ? "you · collaborator" : "collaborator"}</span>
                </div>
                <div className="text-[11px] text-[#6B6A68] truncate">{c.email}</div>
              </div>
              {/* Owner sees a kick button on every collaborator row.
                  Non-owner sees a "Leave" button only on their OWN row. */}
              {isOwner ? (
                <button onClick={() => remove(c.id, c.name || c.email, false)}
                  data-testid={`collab-remove-${c.id}`}
                  className="text-[#6B6A68] hover:text-[#C16E5A]" title="Remove from silo">
                  <Trash size={14} />
                </button>
              ) : isMe ? (
                <button onClick={() => remove(c.id, "yourself", true)}
                  data-testid="collab-leave-btn"
                  className="text-xs px-2 py-1 rounded border hairline text-[#C16E5A] hover:border-[#C16E5A]">
                  Leave
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Pending invites */}
      {pending.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Pending invites</div>
          <div className="space-y-1.5">
            {pending.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 bg-[#F8F4E8] border border-[#E5C77F] rounded px-3 py-2 text-sm" data-testid={`pending-invite-${inv.id}`}>
                <Circle size={12} className="text-[#C19A4A]" />
                <div className="flex-1 min-w-0">
                  <div className="text-[#1C1B1A] truncate">{inv.invitee?.name || inv.invitee?.email}{inv.is_child && <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A5A00] ml-2">child</span>}</div>
                  <div className="text-[11px] text-[#6B6A68]">waiting for them to accept</div>
                </div>
                {isOwner && (
                  <button onClick={() => cancelInvite(inv.id)} data-testid={`cancel-invite-${inv.id}`}
                    className="text-[#6B6A68] hover:text-[#C16E5A]" title="Cancel invite">
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Family-restricted members (Phase C) — guardian release controls. */}
      {isOwner && familyMembers.length > 0 && (
        <div className="mt-4" data-testid="family-members-section">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5 flex items-center gap-1">
            <ShieldWarning size={11} className="text-[#8A5A00]" /> Family-restricted members
          </div>
          <div className="space-y-1.5">
            {familyMembers.map((m) => (
              <div key={m.link_id} className="flex items-center gap-3 bg-[#F8F1E2] border border-[#E5C77F] rounded px-3 py-2 text-sm" data-testid={`family-member-${m.link_id}`}>
                <ShieldWarning size={14} weight="duotone" className="text-[#8A5A00]" />
                <div className="flex-1 min-w-0">
                  <div className="text-[#1C1B1A] truncate">{m.child_name}</div>
                  <div className="text-[11px] text-[#6B6A68] truncate">
                    {m.release_requested ? "asked to be released — approve below" : "purchase guardrails on"}
                  </div>
                </div>
                <button onClick={() => releaseFamilyMember(m)} data-testid={`release-member-${m.link_id}`}
                  className={`text-xs px-2.5 py-1.5 rounded-md border hairline flex items-center gap-1 ${m.release_requested ? "bg-[#2F6B3A] text-white border-transparent hover:opacity-90" : "text-[#4A5D4E] hover:border-[#4A5D4E]"}`}>
                  Mark as adult
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite controls + revenue split — owner only, gated by silo_collab_settings */}
      {isOwner && (
        <LockGate locked={collabLocked} lockStyle={cpLockStyle} onLockedClick={() => cpOpenGate("silo_collab_settings")}>
        <div className="mt-5" data-testid="collab-owner-controls">
          {/* Feb 19, 2026 (Randy) — silo-type shortcut. Right above the
              invite box so if a user is about to add a colleague to what
              they think is a "team" silo but it's actually an Organizer,
              they see the pill + a one-tap Change button before sending
              the invite. Prevents the family-mode-by-mistake trap. */}
          <div className="mb-4 flex items-center gap-2 flex-wrap text-[11px] text-[#6B6A68] bg-[#F4F1E9] border hairline rounded-md px-3 py-2" data-testid="collab-silo-type-hint">
            <span>Sharing a</span>
            <SiloKindPill silo={silo} size="xs" />
            <span className="text-[#8A8A88]">— {(SILO_KIND_META[resolveSiloKind(silo)] || SILO_KIND_META.public).blurb}</span>
            <button
              type="button"
              onClick={() => setTypeConvertOpen(true)}
              data-testid="collab-change-silo-type-btn"
              className="ml-auto text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded border hairline text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1"
            >
              <Sparkle size={10} weight="duotone" /> Change type
            </button>
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5 flex items-center gap-1">
            <UserPlus size={11} /> Invite someone
          </div>
          <div className="relative">
            <div className="flex items-center gap-2 border hairline rounded-md px-3 py-2 bg-white">
              <MagnifyingGlass size={14} className="text-[#6B6A68]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by email or name…"
                data-testid="collab-search-input"
                className="flex-1 text-sm focus:outline-none bg-transparent"
              />
              {searching && <Spinner size={12} className="animate-spin text-[#6B6A68]" />}
            </div>
            {results.length > 0 && (
              <div className="absolute z-10 mt-1 left-0 right-0 bg-white border hairline rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid="collab-search-results">
                {results.map((u) => (
                  <button key={u.id} onClick={() => invite(u)} data-testid={`collab-search-result-${u.id}`}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-[#F0F1EE] border-b hairline last:border-b-0">
                    <UserPlus size={13} className="text-[#4A5D4E]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1C1B1A] truncate">{u.name || u.email}</div>
                      <div className="text-[11px] text-[#6B6A68] truncate">{u.email}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E]">invite →</span>
                  </button>
                ))}
              </div>
            )}
            {!searching && (query || "").trim().length >= 2 && results.length === 0 && (
              <div className="mt-1 text-[11px] text-[#6B6A68] italic">No matches. They may need to sign up first.</div>
            )}
          </div>

          {/* Family invite options — personal silos only (Phase B/C). */}
          {isPersonal && (
            <div className="mt-4 bg-white border hairline rounded-md p-3" data-testid="family-invite-options">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="invite-as-child-label">
                <input type="checkbox" checked={inviteAsChild}
                  onChange={(e) => { setInviteAsChild(e.target.checked); setShareUrl(""); }}
                  data-testid="invite-as-child-checkbox" className="mt-0.5 accent-[#4A5D4E]" />
                <span className="text-[12px] text-[#1C1B1A] leading-relaxed">
                  <span className="flex items-center gap-1 font-medium">
                    <ShieldWarning size={13} weight="duotone" className="text-[#8A5A00]" /> This invite is for my child
                  </span>
                  <span className="text-[#6B6A68]">
                    Their account gets purchase guardrails — checkouts always need a card typed fresh
                    (no saved cards). Nothing else is limited. A parent can release it anytime, and it
                    lifts automatically if they leave the household. Established paying adults can't be
                    stamped — the restriction is dropped for them automatically.
                  </span>
                </span>
              </label>

              <div className="mt-3 border-t hairline pt-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5 flex items-center gap-1">
                  <ShareNetwork size={11} /> Or share an invite link
                </div>
                {!shareUrl ? (
                  <button onClick={mintShareLink} disabled={minting} data-testid="mint-family-link"
                    className="text-xs px-3 py-2 rounded-md border hairline text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1.5 disabled:opacity-60">
                    {minting ? <Spinner size={13} className="animate-spin" /> : <ShareNetwork size={13} />}
                    Create {inviteAsChild ? "a child " : "an "}invite link
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <input readOnly value={shareUrl} data-testid="family-link-url"
                      onFocus={(e) => e.target.select()}
                      className="flex-1 text-[11px] px-2 py-1.5 border hairline rounded bg-[#FDFCF8] text-[#6B6A68] truncate" />
                    <button onClick={copyOrShare} data-testid="copy-family-link"
                      className="text-xs px-3 py-1.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 shrink-0">
                      <Copy size={13} /> Copy / share
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      {/* Equal split toggle + ledger — owner only */}
        <div className="mt-6 border-t hairline pt-4">
          <div className="flex items-start gap-3">
            <Scales size={18} weight="duotone" className="text-[#4A5D4E] mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <strong className="text-sm text-[#1C1B1A]">Split fan-sub income equally</strong>
                <InfoTip text="When ON, every paid fan subscription on this silo gets split equally between you and your collaborator(s). With one collaborator that's 50/50. With two, it's three-way (you keep ⅓, each gets ⅓). And so on. Your share already lands in your Stripe Express account; the others are queued in the ledger until you click Settle now." />
              </div>
              {(() => {
                const n = (data.collaborators || []).length + 1;
                const pct = Math.floor(10000 / n) / 100;
                return (
                  <p className="text-[11px] text-[#6B6A68] mt-0.5">
                    {data.revenue_split_enabled
                      ? `Currently ON. Each fan-sub payment is split ${n} ways — every person earns ${pct.toFixed(2)}% of the net.`
                      : "Currently OFF. 100% of fan-sub net flows to your Stripe Express account."}
                  </p>
                );
              })()}
            </div>
            <button onClick={toggleSplit} data-testid="collab-split-toggle"
              className={`text-xs px-3 py-1.5 rounded-md shrink-0 ${data.revenue_split_enabled ? "bg-[#2F6B3A] text-white hover:opacity-90" : "bg-white border hairline text-[#1C1B1A] hover:border-[#4A5D4E]"}`}>
              {data.revenue_split_enabled ? "ON" : "OFF"}
            </button>
          </div>

          {/* Per-collaborator ledger — only meaningful when there's history. */}
          {(splitSummary?.per_collaborator?.length || 0) > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Split ledger</div>
              <div className="space-y-1">
                {splitSummary.per_collaborator.map((row) => (
                  <div key={row.user_id} className="flex items-center gap-3 bg-white border hairline rounded px-3 py-2 text-sm" data-testid={`split-row-${row.user_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[#1C1B1A] truncate">{row.name || row.email || row.user_id}</div>
                      <div className="text-[11px] text-[#6B6A68]">
                        owed <strong>${(row.owed_cents / 100).toFixed(2)}</strong> · settled <span className="text-[#2F6B3A]">${(row.settled_cents / 100).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Settle button — only enabled if anyone is owed. */}
              {splitSummary.per_collaborator.some((r) => r.owed_cents > 0) && (
                <button onClick={settleNow} disabled={settling} data-testid="settle-splits-btn"
                  className="mt-3 text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50">
                  {settling ? <Spinner size={12} className="animate-spin" /> : <Scales size={12} weight="duotone" />}
                  {settling ? "Sending transfers…" : "Settle now via Stripe"}
                </button>
              )}
              <p className="text-[10px] text-[#6B6A68] italic mt-1.5">
                "Settle" moves the owed amounts from your Stripe Express account to your collaborator's Stripe Express account. They need to have finished Stripe onboarding for the transfer to succeed.
              </p>
            </div>
          )}
        </div>
        </LockGate>
      )}

      {/* Collaborator-side view of their own owed/settled balance on this silo */}
      {!isOwner && (splitSummary?.per_collaborator?.length || 0) > 0 && (
        <div className="mt-6 border-t hairline pt-4" data-testid="collab-earnings">
          <div className="flex items-center gap-2 mb-2">
            <Scales size={16} weight="duotone" className="text-[#4A5D4E]" />
            <strong className="text-sm text-[#1C1B1A]">Your share of this silo</strong>
          </div>
          {splitSummary.per_collaborator.map((row) => (
            <div key={row.user_id} className="text-[12px] text-[#1C1B1A] bg-white border hairline rounded px-3 py-2">
              owed <strong>${(row.owed_cents / 100).toFixed(2)}</strong> · settled <span className="text-[#2F6B3A]">${(row.settled_cents / 100).toFixed(2)}</span>
              <div className="text-[10px] text-[#6B6A68] mt-0.5 italic">
                Owed amounts settle when the silo owner clicks "Settle now" — or you can ask them to.
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
    {typeConvertOpen && (
      <SiloKindConvertModal
        siloId={silo.id}
        currentKind={silo.silo_kind || "public"}
        currentYtTwin={!!silo.is_youtube_twin}
        onClose={() => setTypeConvertOpen(false)}
        onSaved={(updated) => { setTypeConvertOpen(false); onSiloUpdated && onSiloUpdated(updated); }}
      />
    )}
    </>
  );
}
